// SEO-optimized landing page for "Graffiti entfernen Hamburg".
// Targets: Hausverwaltungen, retail/shop owners, property owners, public
// institutions reacting to fresh vandalism. Heavy focus on emergency response,
// gentle methods, and city coverage (Wandsbek, Eimsbüttel, Altona, etc.).
import { Link } from "react-router-dom";
import {
  Sparkles, Phone, ArrowRight, CheckCircle2, Clock, ShieldCheck,
  Building2, AlertTriangle, Camera, Brush, Eye, MapPin,
} from "lucide-react";
import SEOHead from "../components/SEOHead";
import { BUSINESS_PROFILE } from "../data/seo";
import { useLang } from "../contexts/LangContext";

// Districts we typically reach within 90 minutes — used for SEO long-tail.
const HAMBURG_DISTRICTS = [
  "Wandsbek", "Eimsbüttel", "Altona", "Harburg", "Bergedorf", "Nord",
  "Mitte", "Volksdorf", "Rahlstedt", "Bramfeld", "Farmsen", "Eppendorf",
  "Winterhude", "St. Pauli", "Ottensen",
];

// Service-specific FAQ — these are the actual buying questions Hausverwaltungen
// ask before placing an order. Answering them upfront removes objections.
const FAQ = [
  {
    q: "Wie schnell kommt Talkani zur Graffiti-Entfernung?",
    a: "Für Hausverwaltungen mit Vertrag: innerhalb 24 Stunden (Mo–Sa). Notfall-Service in Hamburg-Mitte, Wandsbek, Eimsbüttel & Altona meist binnen 4 Stunden — auf Anfrage 24/7.",
  },
  {
    q: "Beschädigt die Reinigung den Putz oder die Fassade?",
    a: "Nein. Wir wählen das Verfahren je nach Untergrund (Klinker, Putz, Glas, Metall, Holz). Bei sensiblen Oberflächen nutzen wir Niederdruck-Heißwasser + biologische Lösungsmittel ohne aggressive Säuren. Vor jeder Behandlung machen wir einen Testpatch.",
  },
  {
    q: "Was kostet die Graffiti-Entfernung in Hamburg?",
    a: "Ab €149 für eine Standard-Fläche bis 2 m² (z. B. Eingangstür, Briefkasten-Bereich). Größere Fassaden ab €15/m² inkl. Material und Entsorgung. Für Hausverwaltungen: monatliche Sammelrechnung möglich.",
  },
  {
    q: "Macht ihr auch Anti-Graffiti-Schutz?",
    a: "Ja. Nach der Reinigung tragen wir auf Wunsch eine transparente Schutzbeschichtung auf. Bei erneutem Tag lässt sich das Graffiti dann in 15 Minuten mit Wasser abwischen — ohne Spezialgerät.",
  },
  {
    q: "Stellen ihr eine Bescheinigung für die Versicherung aus?",
    a: "Selbstverständlich. Bei jedem Auftrag erhalten Sie: Vorher-/Nachher-Fotos, Datum/Uhrzeit, eingesetztes Verfahren und eine Rechnung im DATEV-Format — alles, was Sie für die Gebäudeversicherung benötigen.",
  },
  {
    q: "Auch nachts oder am Wochenende einsatzbereit?",
    a: "Ja. Für Geschäftskunden mit Wartungsvertrag bieten wir 24/7-Notfalleinsätze — wichtig bei beleidigenden oder politisch heiklen Inhalten, die schnell entfernt werden müssen.",
  },
];

const METHODS = [
  {
    icon: Brush,
    title: "Niederdruck-Heißwasser",
    text: "Für Klinker, Beton, Stein. 95°C + 60 bar — entfernt Sprayfarben ohne aggressive Chemie.",
  },
  {
    icon: Sparkles,
    title: "Biologische Lösungsmittel",
    text: "Für empfindliche Putze und gestrichene Flächen. VOC-frei, dermatologisch unbedenklich.",
  },
  {
    icon: Eye,
    title: "Soda-Strahlen",
    text: "Für historische Fassaden und Denkmäler. Schonende Trockenstrahlung mit Natriumbicarbonat.",
  },
  {
    icon: ShieldCheck,
    title: "Anti-Graffiti-Schutz",
    text: "Transparente Versiegelung nach der Reinigung — beim nächsten Tag genügt Wasser.",
  },
];

const URGENCY = [
  {
    icon: AlertTriangle,
    title: "Mietminderungs-Risiko",
    text: "Mieter dürfen die Miete bei sichtbarem Graffiti am Hauseingang um bis zu 5 % mindern — schnelles Handeln spart deutlich mehr als die Reinigung kostet.",
  },
  {
    icon: Building2,
    title: "Hausverwaltungs-Pflicht",
    text: "Nach BGB § 535 ist der Vermieter zur Erhaltung verpflichtet. Verzögerte Reinigung kann zu Eigentümer-Beschwerden und Schadenersatz-Forderungen führen.",
  },
  {
    icon: Eye,
    title: "Beleidigende oder hetzerische Inhalte",
    text: "Politisch oder strafrechtlich relevante Tags müssen umgehend entfernt werden — wir reagieren bei solchen Notfällen innerhalb von 4 Stunden in Hamburg.",
  },
];

export default function GraffitiEntfernungHamburg() {
  const { } = useLang(); // Reserved for future i18n; this SEO page is DE-only.

  // Schema.org LocalBusiness Service markup — improves rich-snippet chances.
  const schema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "Graffiti-Entfernung",
    provider: {
      "@type": "LocalBusiness",
      name: BUSINESS_PROFILE.name,
      telephone: BUSINESS_PROFILE.telephone,
      email: BUSINESS_PROFILE.email,
      address: {
        "@type": "PostalAddress",
        streetAddress: BUSINESS_PROFILE.street,
        addressLocality: BUSINESS_PROFILE.city,
        postalCode: BUSINESS_PROFILE.postalCode,
        addressCountry: BUSINESS_PROFILE.country,
      },
    },
    areaServed: { "@type": "City", name: "Hamburg" },
    offers: {
      "@type": "Offer",
      priceCurrency: "EUR",
      price: "149",
      priceSpecification: {
        "@type": "UnitPriceSpecification",
        priceCurrency: "EUR",
        price: "15",
        unitText: "m²",
      },
    },
    description:
      "Schonende Graffiti-Entfernung in Hamburg für Hausverwaltungen, Einzelhandel und Privatkunden. Notfall-Service binnen 4 Stunden, biologische Verfahren ohne Fassadenschäden.",
  };

  return (
    <div data-testid="graffiti-page" lang="de">
      <SEOHead
        title="Graffiti entfernen Hamburg · 24 h Notfall · ab €149 — Talkani"
        description="Professionelle Graffiti-Entfernung in Hamburg für Hausverwaltungen, Geschäfte & Privatkunden. Schonende Verfahren, Anti-Graffiti-Schutz, Sammelrechnung. Notfall in 4 Std."
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

      {/* ===== HERO ===== */}
      <section className="relative bg-[#090A0B] text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20 bg-cover bg-center" style={{ backgroundImage: "url(/generated/reinigung.png)" }} />
        <div className="absolute inset-0 bg-gradient-to-br from-[#090A0B] via-[#090A0B]/95 to-transparent" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-2 text-[#FF5A00] mb-4">
            <Sparkles size={16} />
            <span className="text-xs font-bold tracking-[0.3em] uppercase">Graffiti entfernen · Hamburg</span>
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter mb-6 leading-[0.95] max-w-4xl">
            Graffiti weg —<br />schonend, schnell, dokumentiert.
          </h1>
          <p className="text-lg sm:text-xl text-neutral-300 max-w-2xl mb-10 leading-relaxed">
            Professionelle Graffiti-Entfernung in ganz Hamburg. Für Hausverwaltungen, Einzelhandel,
            Schulen und Privateigentümer. <strong className="text-white">Notfall-Einsatz in 4&nbsp;Stunden</strong>,
            biologische Verfahren ohne Fassadenschäden, Vorher-/Nachher-Foto-Doku für die Versicherung.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/book?service=reinigung&sub=Graffiti-Entfernung"
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold uppercase tracking-wide px-7 py-4 transition rounded-sm inline-flex items-center gap-2"
              data-testid="graffiti-book-cta"
            >
              Jetzt buchen <ArrowRight size={16} />
            </Link>
            <a
              href={`tel:${BUSINESS_PROFILE.telephone.replace(/[\s-]/g, "")}`}
              className="border border-white/30 hover:border-white text-white font-bold uppercase tracking-wide px-7 py-4 transition rounded-sm inline-flex items-center gap-2"
              data-testid="graffiti-call-cta"
            >
              <Phone size={16} /> Notfall anrufen
            </a>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-neutral-300">
            <span className="flex items-center gap-1.5"><Clock size={14} className="text-[#FF5A00]" /> 24 Std. Reaktion</span>
            <span className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#FF5A00]" /> Foto-Doku inklusive</span>
            <span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-[#FF5A00]" /> Versicherungs-Bescheinigung</span>
            <span className="flex items-center gap-1.5"><MapPin size={14} className="text-[#FF5A00]" /> Hamburg & Umland</span>
          </div>
        </div>
      </section>

      {/* ===== WHY SPEED MATTERS ===== */}
      <section className="py-20 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#FF5A00] mb-3">Warum schnell handeln?</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-10 max-w-3xl">
            Jeder Tag Verzögerung kostet bares Geld.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {URGENCY.map((u) => (
              <div key={u.title} className="border-l-4 border-[#FF5A00] bg-orange-50/50 p-7" data-testid={`urgency-${u.title}`}>
                <u.icon size={26} className="text-[#FF5A00] mb-3" />
                <div className="font-black tracking-tight text-lg mb-2">{u.title}</div>
                <p className="text-sm text-neutral-700 leading-relaxed">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== METHODS ===== */}
      <section className="py-20 lg:py-28 bg-[#F8F9FA] border-y border-neutral-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#FF5A00] mb-3">Unsere Verfahren</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-4 max-w-3xl">
            Die richtige Methode für jeden Untergrund.
          </h2>
          <p className="text-base text-neutral-600 max-w-2xl mb-10 leading-relaxed">
            Vor jedem Auftrag prüfen wir den Untergrund (Klinker, Putz, Beton, Glas, Holz, Metall, Naturstein)
            und wählen das schonendste wirksame Verfahren.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {METHODS.map((m) => (
              <div key={m.title} className="bg-white border border-neutral-200 p-6 hover:border-[#FF5A00] transition" data-testid={`method-${m.title}`}>
                <m.icon size={24} className="text-[#FF5A00] mb-3" />
                <div className="font-black tracking-tight mb-2">{m.title}</div>
                <p className="text-xs text-neutral-600 leading-relaxed">{m.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PROCESS ===== */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#FF5A00] mb-3">Ablauf</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter mb-12 max-w-3xl">
            In 4 Schritten zur sauberen Fassade.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <ProcessStep n="01" title="Anfrage & Foto" text="Sie senden uns ein Foto der betroffenen Fläche per E-Mail, WhatsApp oder über das Buchungsformular." />
            <ProcessStep n="02" title="Festpreis-Angebot" text="Wir senden Ihnen innerhalb 2 Stunden ein verbindliches Angebot mit Termin." />
            <ProcessStep n="03" title="Reinigung vor Ort" text="Unser geschultes Team rückt mit professionellem Equipment an. Dauer: 30 Min. bis 4 Stunden je nach Fläche." />
            <ProcessStep n="04" title="Doku & Rechnung" text="Vorher-/Nachher-Fotos, Verfahrens-Bericht und DATEV-Rechnung — perfekt für Versicherung und Buchhaltung." />
          </div>
        </div>
      </section>

      {/* ===== DISTRICTS COVERAGE (SEO LONG-TAIL) ===== */}
      <section className="py-20 lg:py-24 bg-[#F8F9FA] border-y border-neutral-200">
        <div className="max-w-5xl mx-auto px-6 lg:px-12 text-center">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#FF5A00] mb-3">Wo wir aktiv sind</p>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-4">
            Graffiti entfernen in jedem Hamburger Stadtteil.
          </h2>
          <p className="text-base text-neutral-600 max-w-2xl mx-auto mb-10 leading-relaxed">
            Express-Einsatz binnen 4 Stunden in allen Bezirken. Wir kommen zu Ihnen in:
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            {HAMBURG_DISTRICTS.map((d) => (
              <span key={d} className="inline-flex items-center gap-1.5 bg-white border border-neutral-200 px-4 py-2 text-sm font-medium" data-testid={`district-${d}`}>
                <MapPin size={13} className="text-[#FF5A00]" /> {d}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#FF5A00] mb-3 text-center">Preise</p>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-10 text-center">Faire Festpreise — keine versteckten Kosten.</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <PriceTier label="Standard" desc="Bis 2 m² — z. B. Tür, Briefkasten-Bereich, einzelner Tag" price="€149" features={["Inkl. Anfahrt Hamburg", "Foto-Doku", "Festpreis"]} />
            <PriceTier label="Fassade" desc="Größere Flächen pro m²" price="€15/m²" features={["Inkl. Material", "Inkl. Entsorgung", "Mengenstaffel"]} highlight />
            <PriceTier label="Hausverwaltungs-Vertrag" desc="Monatliche Pauschale für ganzes Portfolio" price="auf Anfrage" features={["24/7-Notdienst", "Sammelrechnung", "Bis −20 % Rabatt"]} />
          </div>
          <p className="text-xs text-neutral-500 text-center mt-6 leading-relaxed">
            Alle Preise zzgl. MwSt. Anti-Graffiti-Schutzbeschichtung ab €12/m² zubuchbar. Notdienst-Aufschlag außerhalb 07:00–20:00.
          </p>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="py-20 lg:py-28 bg-[#F8F9FA] border-y border-neutral-200">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-[#FF5A00] mb-3 text-center">Häufige Fragen</p>
          <h2 className="text-3xl sm:text-4xl font-black tracking-tighter mb-10 text-center">Antworten auf das, was Auftraggeber fragen.</h2>
          <div className="space-y-3">
            {FAQ.map((f) => (
              <details key={f.q} className="group bg-white border border-neutral-200 p-5 cursor-pointer hover:border-[#FF5A00] transition" data-testid={`faq-${f.q.slice(0, 20)}`}>
                <summary className="font-bold text-base flex items-center justify-between list-none">
                  <span>{f.q}</span>
                  <span className="text-[#FF5A00] text-2xl font-black group-open:rotate-45 transition">+</span>
                </summary>
                <p className="text-sm text-neutral-700 leading-relaxed mt-3 pt-3 border-t border-neutral-100">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-20 bg-[#090A0B] text-white text-center">
        <div className="max-w-3xl mx-auto px-6">
          <Camera size={36} className="text-[#FF5A00] mx-auto mb-5" />
          <h2 className="text-4xl sm:text-5xl font-black tracking-tighter mb-4">Foto schicken, Festpreis in 2 Stunden.</h2>
          <p className="text-neutral-300 mb-8">
            Senden Sie uns ein Bild der Stelle — wir antworten mit verbindlichem Angebot &amp; Termin.
          </p>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link
              to="/book?service=reinigung&sub=Graffiti-Entfernung"
              className="bg-[#FF5A00] hover:bg-[#E04F00] text-white font-bold uppercase tracking-wide px-7 py-4 transition inline-flex items-center gap-2"
              data-testid="graffiti-final-book"
            >
              Jetzt anfragen <ArrowRight size={16} />
            </Link>
            <a
              href={`https://wa.me/${BUSINESS_PROFILE.telephone.replace(/[+\s-]/g, "")}?text=${encodeURIComponent("Hallo Talkani, ich habe ein Graffiti-Problem in Hamburg. Anbei das Foto.")}`}
              target="_blank"
              rel="noreferrer"
              className="border border-white/30 hover:border-white text-white font-bold uppercase tracking-wide px-7 py-4 transition inline-flex items-center gap-2"
              data-testid="graffiti-whatsapp"
            >
              WhatsApp senden
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

const ProcessStep = ({ n, title, text }) => (
  <div className="relative pt-6">
    <div className="absolute top-0 left-0 text-7xl font-black text-orange-100 leading-none -z-0 select-none">{n}</div>
    <div className="relative z-10 pt-6">
      <div className="font-black tracking-tight text-lg mb-2">{title}</div>
      <p className="text-sm text-neutral-600 leading-relaxed">{text}</p>
    </div>
  </div>
);

const PriceTier = ({ label, desc, price, features, highlight = false }) => (
  <div className={`p-7 border-2 ${highlight ? "border-[#FF5A00] bg-orange-50" : "border-neutral-200 bg-white"}`} data-testid={`price-tier-${label}`}>
    <div className="text-xs font-bold tracking-widest uppercase text-neutral-500 mb-1">{desc}</div>
    <div className="text-2xl font-black tracking-tight mb-4">{label}</div>
    <div className="text-4xl font-black text-[#FF5A00] tabular-nums mb-4">{price}</div>
    <ul className="space-y-1.5 text-sm text-neutral-700">
      {features.map((f) => (
        <li key={f} className="flex items-center gap-1.5">
          <CheckCircle2 size={14} className="text-[#FF5A00] flex-shrink-0" /> {f}
        </li>
      ))}
    </ul>
  </div>
);
